import { useState, useCallback } from "react";
import {
  BarChart2, TrendingDown, Users, Wrench, Trash2, Package,
  PackageSearch, AlertTriangle,
} from "lucide-react";
import { assetApi } from "../api";
import type { DisposalListParams, MaintenanceListParams, AssetListParams } from "../api";
import { useAsyncData, usePagination } from "../hooks";
import {
  AssetError, AssetLoading, AssetStatusBadge, BackBtn, Btn,
  Card, CardHeader, ConditionBadge, ExportBtn,
  FilterPanel, PageHeader, Pagination, SearchInput, SelectFilter,
  Table, TBody, TD, TH, THead, TR, fmt, fmtDate, ActiveFilterBadges,
} from "../components/AssetUI";
import {
  downloadXls,
  ASSET_EXPORT_HEADERS, assetToRow,
  DISPOSAL_EXPORT_HEADERS, disposalToRow,
  MAINTENANCE_EXPORT_HEADERS, maintenanceToRow,
} from "../utils/export";

// ─── Tab definitions ──────────────────────────────────────────────────────────

type ReportTab = {
  id:    string;
  label: string;
  icon:  React.ComponentType<{ className?: string }>;
  desc:  string;
};

const TABS: ReportTab[] = [
  { id: "overview",    label: "Overview",       icon: BarChart2,    desc: "Fleet summary by category & status" },
  { id: "register",    label: "Asset Register", icon: PackageSearch,desc: "Full paginated asset list with filters" },
  { id: "assignments", label: "Assignments",    icon: Users,        desc: "Currently assigned assets" },
  { id: "depreciation",label: "Depreciation",   icon: TrendingDown, desc: "Cost vs current value analysis" },
  { id: "maintenance", label: "Maintenance",    icon: Wrench,       desc: "Maintenance log with status filters" },
  { id: "disposal",    label: "Disposals",      icon: Trash2,       desc: "Disposal records and value recovered" },
];

// ─── Root ─────────────────────────────────────────────────────────────────────

export default function AssetReportsPage() {
  const [activeTab, setActiveTab] = useState("overview");
  const tab = TABS.find((t) => t.id === activeTab)!;

  return (
    <div className="flex min-h-0 flex-col gap-6">
      {/* Page header */}
      <PageHeader
        breadcrumb="Asset Management"
        title="Reports & Analytics"
        actions={<BackBtn to="/assets" label="Dashboard" />}
      />

      {/* Tab navigation */}
      <div className="flex gap-1.5 overflow-x-auto rounded-2xl border border-slate-100 bg-slate-50/70 p-1.5">
        {TABS.map((t) => {
          const Icon = t.icon;
          const active = t.id === activeTab;
          return (
            <button
              key={t.id}
              type="button"
              onClick={() => setActiveTab(t.id)}
              className={`group flex shrink-0 items-center gap-2 rounded-xl px-4 py-2.5 text-sm font-semibold transition-all
                ${active
                  ? "bg-white text-indigo-700 shadow-sm ring-1 ring-indigo-100"
                  : "text-slate-500 hover:bg-white/70 hover:text-slate-700"}`}
            >
              <Icon className={`h-4 w-4 shrink-0 ${active ? "text-indigo-600" : "text-slate-400 group-hover:text-slate-500"}`} />
              {t.label}
            </button>
          );
        })}
      </div>

      {/* Active tab desc */}
      <div className="flex items-center gap-2 text-sm text-slate-500">
        <tab.icon className="h-4 w-4 shrink-0 text-indigo-400" />
        {tab.desc}
      </div>

      {/* Report content */}
      {activeTab === "overview"     && <OverviewReport />}
      {activeTab === "register"     && <RegisterReport />}
      {activeTab === "assignments"  && <AssignmentsReport />}
      {activeTab === "depreciation" && <DepreciationReport />}
      {activeTab === "maintenance"  && <MaintenanceReport />}
      {activeTab === "disposal"     && <DisposalReport />}
    </div>
  );
}

// ─── Stat card primitive ──────────────────────────────────────────────────────

function Stat({ label, value, sub, accent }: { label: string; value: string | number; sub?: string; accent?: string }) {
  return (
    <div className="rounded-2xl border bg-white p-5 shadow-sm">
      <p className="text-xs font-medium text-slate-400">{label}</p>
      <p className={`mt-1 text-2xl font-bold ${accent ?? "text-slate-800"}`}>{value}</p>
      {sub && <p className="mt-0.5 text-xs text-slate-400">{sub}</p>}
    </div>
  );
}

// ─── Overview (category + status breakdown) ───────────────────────────────────

function OverviewReport() {
  const stats  = useAsyncData(() => assetApi.dashboardStats(), []);
  const cats   = useAsyncData(() => assetApi.listCategories(), []);

  const doExportStatus = useCallback(() => {
    if (!stats.data) return;
    const d = stats.data;
    downloadXls("status-breakdown.xls", "Status Distribution",
      ["Status", "Count"],
      [
        ["Available",         d.availableAssets],
        ["Assigned",          d.assignedAssets],
        ["Under Maintenance", d.underMaintenanceAssets],
        ["Pending Disposal",  d.pendingDisposalAssets],
        ["Disposed",          d.disposedAssets],
        ["Flagged",           d.flaggedForDisposal],
      ],
    );
  }, [stats.data]);

  const doExportCat = useCallback(() => {
    downloadXls("category-breakdown.xls", "Category Breakdown",
      ["Category", "Code", "Assets", "Dep Rate %", "Useful Life (yrs)"],
      (cats.data ?? []).map((c) => [c.name, c.code, c.assetCount ?? 0, c.depreciationRate ?? 0, c.usefulLifeYears ?? 0]),
    );
  }, [cats.data]);

  if (stats.loading || cats.loading) return <AssetLoading label="Loading overview…" />;
  if (stats.error) return <AssetError message={stats.error} onRetry={stats.reload} />;

  const d = stats.data!;
  const statusRows = [
    { label: "Available",         count: d.availableAssets,        color: "bg-emerald-500" },
    { label: "Assigned",          count: d.assignedAssets,         color: "bg-blue-500"    },
    { label: "Under Maintenance", count: d.underMaintenanceAssets,  color: "bg-amber-400"   },
    { label: "Pending Disposal",  count: d.pendingDisposalAssets,  color: "bg-orange-400"  },
    { label: "Disposed",          count: d.disposedAssets,         color: "bg-slate-400"   },
    { label: "Flagged",           count: d.flaggedForDisposal,     color: "bg-red-400"     },
  ];
  const fleetTotal = statusRows.reduce((s, r) => s + r.count, 0) || 1;
  const catTotal   = (cats.data ?? []).reduce((s, c) => s + (c.assetCount ?? 0), 0) || 1;

  return (
    <div className="space-y-6">
      {/* KPI row */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Stat label="Total Assets"       value={d.totalAssets}                       />
        <Stat label="Total Purchase Cost" value={fmt(d.totalPurchaseCost)}            />
        <Stat label="Total Current Value" value={fmt(d.totalCurrentValue)} accent="text-emerald-700" />
        <Stat label="Fleet Depreciation"  value={fmt(d.totalPurchaseCost - d.totalCurrentValue)} accent="text-red-600"
          sub={d.totalPurchaseCost ? `${(((d.totalPurchaseCost - d.totalCurrentValue) / d.totalPurchaseCost) * 100).toFixed(1)}% of purchase cost` : undefined} />
      </div>

      {/* Status distribution */}
      <Card noPad>
        <CardHeader title="Status Distribution" subtitle={`${d.totalAssets} assets across fleet`}
          action={<ExportBtn onExport={doExportStatus} />} />
        <div className="divide-y">
          {statusRows.map((r) => (
            <div key={r.label} className="flex items-center gap-4 px-5 py-3.5">
              <span className="w-40 text-sm font-medium text-slate-700 shrink-0">{r.label}</span>
              <div className="flex-1">
                <div className="h-2 overflow-hidden rounded-full bg-slate-100">
                  <div className={`h-2 rounded-full transition-all ${r.color}`}
                    style={{ width: `${(r.count / fleetTotal) * 100}%` }} />
                </div>
              </div>
              <span className="w-10 text-right text-sm font-bold text-slate-700">{r.count}</span>
              <span className="w-10 text-right text-xs text-slate-400">{((r.count / fleetTotal) * 100).toFixed(0)}%</span>
            </div>
          ))}
        </div>
      </Card>

      {/* Category breakdown */}
      <Card noPad>
        <CardHeader title="Category Breakdown" subtitle={`${cats.data?.length ?? 0} categories`}
          action={<ExportBtn onExport={doExportCat} />} />
        {cats.loading ? <div className="px-5 py-4 text-sm text-slate-400">Loading categories…</div>
        : cats.error   ? <div className="px-5 py-4 text-sm text-red-500">{cats.error}</div>
        : (
          <Table>
            <THead>
              <TH>Category</TH><TH>Code</TH><TH right>Assets</TH>
              <TH right>Dep Rate</TH><TH right>Useful Life</TH><TH right>% of Fleet</TH>
            </THead>
            <TBody>
              {(cats.data ?? []).map((c) => (
                <TR key={c.id}>
                  <TD><span className="font-semibold text-slate-800">{c.name}</span></TD>
                  <TD><span className="font-mono text-xs text-indigo-600">{c.code}</span></TD>
                  <TD right><span className="font-bold text-slate-700">{c.assetCount ?? 0}</span></TD>
                  <TD right muted>{c.depreciationRate != null ? `${c.depreciationRate}%` : "—"}</TD>
                  <TD right muted>{c.usefulLifeYears != null ? `${c.usefulLifeYears} yrs` : "—"}</TD>
                  <TD right>
                    <span className="rounded-full bg-indigo-50 px-2 py-0.5 text-xs font-semibold text-indigo-700">
                      {(((c.assetCount ?? 0) / catTotal) * 100).toFixed(1)}%
                    </span>
                  </TD>
                </TR>
              ))}
            </TBody>
          </Table>
        )}
      </Card>
    </div>
  );
}

// ─── Full asset register ──────────────────────────────────────────────────────

function RegisterReport() {
  const pagination = usePagination();
  const [filters, setFilters] = useState<AssetListParams>({ search: "", status: "", categoryId: undefined });
  const cats = useAsyncData(() => assetApi.listCategories(), []);
  const { data, loading, error, reload } = useAsyncData(
    () => assetApi.listAssets(pagination.page, pagination.size, filters),
    [pagination.page, pagination.size, filters],
  );
  function setF<K extends keyof AssetListParams>(k: K, v: AssetListParams[K]) {
    setFilters((f) => ({ ...f, [k]: v })); pagination.reset();
  }

  const activeBadges = [
    filters.status     && { label: `Status: ${filters.status.replace(/_/g," ")}`, onRemove: () => setF("status", "") },
    filters.categoryId && { label: `Category: ${cats.data?.find(c=>c.id===filters.categoryId)?.name ?? filters.categoryId}`, onRemove: () => setF("categoryId", undefined) },
  ].filter(Boolean) as { label: string; onRemove: () => void }[];

  const doExport = useCallback(() => {
    downloadXls(`asset-register-${new Date().toISOString().slice(0,10)}.xls`, "Asset Register",
      ASSET_EXPORT_HEADERS, (data?.content ?? []).map(assetToRow));
  }, [data]);

  return (
    <div className="space-y-4">
      {/* Stats */}
      {data && (
        <div className="grid gap-3 sm:grid-cols-3">
          <Stat label="Total on this page"   value={data.content.length} />
          <Stat label="Total matching"        value={data.totalElements} />
          <Stat label="Pages"                 value={data.totalPages} />
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3">
        <SearchInput value={filters.search ?? ""} onChange={(v) => setF("search", v)}
          placeholder="Search name, code, serial…" className="min-w-60 flex-1" />
        <FilterPanel activeCount={activeBadges.length}
          onClear={() => { setFilters({ search:"",status:"",categoryId:undefined }); pagination.reset(); }}
          onRefresh={reload}>
          <SelectFilter label="All Statuses" value={filters.status ?? ""} onChange={(v) => setF("status", v)}
            options={STATUS_OPTS} />
          <SelectFilter label="All Categories" value={filters.categoryId ? String(filters.categoryId) : ""}
            onChange={(v) => setF("categoryId", v ? Number(v) : undefined)}
            options={(cats.data??[]).map(c=>({label:c.name,value:String(c.id)}))} />
        </FilterPanel>
        <ExportBtn onExport={doExport} />
      </div>
      <ActiveFilterBadges filters={activeBadges} />

      {loading && <AssetLoading label="Loading assets…" />}
      {error   && <AssetError message={error} onRetry={reload} />}

      {!loading && data && (
        <>
          <Card noPad>
            <Table>
              <THead>
                <TH>Asset</TH><TH>Category</TH><TH>Location</TH>
                <TH>Status</TH><TH>Condition</TH><TH>Assigned To</TH>
                <TH right>Purchase Cost</TH><TH right>Current Value</TH>
              </THead>
              <TBody>
                {data.content.length === 0
                  ? <tr><td colSpan={8} className="py-10 text-center text-sm text-slate-400">No assets match your filters.</td></tr>
                  : data.content.map((a) => (
                  <TR key={a.id}>
                    <TD>
                      <div className="font-semibold text-slate-800">{a.name}</div>
                      <div className="font-mono text-xs text-slate-400">{a.assetNumber}</div>
                    </TD>
                    <TD muted>{a.categoryName}</TD>
                    <TD muted>{a.locationName ?? "—"}</TD>
                    <TD><AssetStatusBadge value={a.status} /></TD>
                    <TD><ConditionBadge value={a.condition} /></TD>
                    <TD muted>
                      {a.assignedEmployeeName
                        ? <span className="font-medium text-slate-700">{a.assignedEmployeeName}</span>
                        : "—"}
                    </TD>
                    <TD right muted>{fmt(a.purchaseCost)}</TD>
                    <TD right>
                      <span className="font-medium text-emerald-700">{fmt(a.currentValue)}</span>
                    </TD>
                  </TR>
                ))}
              </TBody>
            </Table>
          </Card>
          <Pagination page={data} onPageChange={pagination.setPage} onSizeChange={pagination.setSize} />
        </>
      )}
    </div>
  );
}

// ─── Assignments (status=ASSIGNED assets) ────────────────────────────────────

function AssignmentsReport() {
  const pagination = usePagination();
  const [search, setSearch]       = useState("");
  const [categoryId, setCategoryId] = useState<number | undefined>();
  const cats = useAsyncData(() => assetApi.listCategories(), []);
  const { data, loading, error, reload } = useAsyncData(
    () => assetApi.listAssets(pagination.page, pagination.size, {
      status: "ASSIGNED",
      search: search || undefined,
      categoryId,
    }),
    [pagination.page, pagination.size, search, categoryId],
  );

  const doExport = useCallback(() => {
    downloadXls(`assignments-${new Date().toISOString().slice(0,10)}.xls`, "Asset Assignments",
      ["Asset No.", "Name", "Category", "Location", "Assigned To", "Condition", "Purchase Cost"],
      (data?.content ?? []).map((a) => [
        a.assetNumber, a.name, a.categoryName, a.locationName ?? "—",
        a.assignedEmployeeName ?? "—", a.condition, a.purchaseCost ?? 0,
      ]),
    );
  }, [data]);

  return (
    <div className="space-y-4">
      {data && (
        <div className="grid gap-3 sm:grid-cols-2">
          <Stat label="Assigned Assets" value={data.totalElements} accent="text-blue-700" />
          <Stat label="Pages"           value={data.totalPages} />
        </div>
      )}

      <div className="flex flex-wrap items-center gap-3">
        <SearchInput value={search} onChange={(v) => { setSearch(v); pagination.reset(); }}
          placeholder="Search asset name, employee…" className="min-w-60 flex-1" />
        <FilterPanel activeCount={categoryId ? 1 : 0}
          onClear={() => { setCategoryId(undefined); pagination.reset(); }}
          onRefresh={reload}>
          <SelectFilter label="All Categories" value={categoryId ? String(categoryId) : ""}
            onChange={(v) => { setCategoryId(v ? Number(v) : undefined); pagination.reset(); }}
            options={(cats.data??[]).map(c=>({label:c.name,value:String(c.id)}))} />
        </FilterPanel>
        <ExportBtn onExport={doExport} />
      </div>

      {loading && <AssetLoading label="Loading assigned assets…" />}
      {error   && <AssetError message={error} onRetry={reload} />}

      {!loading && data && (
        <>
          <Card noPad>
            <Table>
              <THead>
                <TH>Asset</TH><TH>Category</TH><TH>Location</TH>
                <TH>Assigned To</TH><TH>Condition</TH><TH right>Purchase Cost</TH><TH>Flagged</TH>
              </THead>
              <TBody>
                {data.content.length === 0
                  ? <tr><td colSpan={7} className="py-10 text-center text-sm text-slate-400">No assigned assets found.</td></tr>
                  : data.content.map((a) => (
                  <TR key={a.id}>
                    <TD>
                      <div className="font-semibold text-slate-800">{a.name}</div>
                      <div className="font-mono text-xs text-slate-400">{a.assetNumber}</div>
                    </TD>
                    <TD muted>{a.categoryName}</TD>
                    <TD muted>{a.locationName ?? "—"}</TD>
                    <TD><span className="font-semibold text-blue-700">{a.assignedEmployeeName ?? "—"}</span></TD>
                    <TD><ConditionBadge value={a.condition} /></TD>
                    <TD right muted>{fmt(a.purchaseCost)}</TD>
                    <TD>
                      {a.flaggedForDisposal
                        ? <span className="inline-flex items-center gap-1 rounded-full bg-red-50 px-2 py-0.5 text-xs font-semibold text-red-600"><AlertTriangle className="h-3 w-3"/>Yes</span>
                        : <span className="text-slate-300">—</span>}
                    </TD>
                  </TR>
                ))}
              </TBody>
            </Table>
          </Card>
          <Pagination page={data} onPageChange={pagination.setPage} onSizeChange={pagination.setSize} />
        </>
      )}
    </div>
  );
}

// ─── Depreciation ─────────────────────────────────────────────────────────────

function DepreciationReport() {
  const pagination = usePagination();
  const [categoryId, setCategoryId] = useState<number | undefined>();
  const [status, setStatus]         = useState("");
  const cats = useAsyncData(() => assetApi.listCategories(), []);
  const { data, loading, error, reload } = useAsyncData(
    () => assetApi.listAssets(pagination.page, pagination.size, { categoryId, status: status || undefined }),
    [pagination.page, pagination.size, categoryId, status],
  );
  const assets       = data?.content ?? [];
  const totalPurch   = assets.reduce((s, a) => s + (a.purchaseCost ?? 0), 0);
  const totalCurrent = assets.reduce((s, a) => s + (a.currentValue  ?? 0), 0);

  const doExport = useCallback(() => {
    downloadXls("depreciation.xls", "Asset Depreciation",
      ["Asset No.", "Name", "Category", "Purchase Cost", "Current Value", "Depreciation", "Dep %"],
      assets.map((a) => {
        const loss = (a.purchaseCost ?? 0) - (a.currentValue ?? 0);
        return [a.assetNumber, a.name, a.categoryName, a.purchaseCost??0, a.currentValue??0, loss,
          a.purchaseCost ? `${((loss/a.purchaseCost)*100).toFixed(1)}%` : "—"];
      }),
    );
  }, [assets]);

  return (
    <div className="space-y-4">
      <div className="grid gap-4 sm:grid-cols-3">
        <Stat label="Total Purchase Cost" value={fmt(totalPurch)} />
        <Stat label="Total Current Value" value={fmt(totalCurrent)} accent="text-emerald-700" />
        <Stat label="Total Depreciation"  value={fmt(totalPurch - totalCurrent)} accent="text-red-600"
          sub={totalPurch ? `${(((totalPurch-totalCurrent)/totalPurch)*100).toFixed(1)}%` : undefined} />
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <FilterPanel activeCount={(categoryId ? 1 : 0) + (status ? 1 : 0)}
          onClear={() => { setCategoryId(undefined); setStatus(""); pagination.reset(); }}
          onRefresh={reload}>
          <SelectFilter label="All Categories" value={categoryId ? String(categoryId) : ""}
            onChange={(v) => { setCategoryId(v ? Number(v) : undefined); pagination.reset(); }}
            options={(cats.data??[]).map(c=>({label:c.name,value:String(c.id)}))} />
          <SelectFilter label="All Statuses" value={status} onChange={(v) => { setStatus(v); pagination.reset(); }}
            options={STATUS_OPTS} />
        </FilterPanel>
        <ExportBtn onExport={doExport} />
      </div>

      {loading && <AssetLoading label="Loading…" />}
      {error   && <AssetError message={error} onRetry={reload} />}

      {!loading && data && (
        <>
          <Card noPad>
            <Table>
              <THead>
                <TH>Asset</TH><TH>Category</TH><TH>Status</TH>
                <TH right>Purchase Cost</TH><TH right>Current Value</TH>
                <TH right>Depreciation</TH><TH right>Dep %</TH>
              </THead>
              <TBody>
                {assets.length === 0
                  ? <tr><td colSpan={7} className="py-10 text-center text-sm text-slate-400">No assets found.</td></tr>
                  : assets.map((a) => {
                  const loss = (a.purchaseCost ?? 0) - (a.currentValue ?? 0);
                  const pct  = a.purchaseCost ? (loss / a.purchaseCost) * 100 : 0;
                  return (
                    <TR key={a.id}>
                      <TD>
                        <div className="font-semibold text-slate-800">{a.name}</div>
                        <div className="font-mono text-xs text-slate-400">{a.assetNumber}</div>
                      </TD>
                      <TD muted>{a.categoryName}</TD>
                      <TD><AssetStatusBadge value={a.status} /></TD>
                      <TD right muted>{fmt(a.purchaseCost)}</TD>
                      <TD right><span className="font-medium text-emerald-700">{fmt(a.currentValue)}</span></TD>
                      <TD right><span className="font-medium text-red-600">{fmt(loss)}</span></TD>
                      <TD right>
                        <span className={`rounded-full px-2.5 py-0.5 text-xs font-bold
                          ${pct > 75 ? "bg-red-100 text-red-700"
                          : pct > 50 ? "bg-orange-100 text-orange-700"
                          : pct > 25 ? "bg-amber-100 text-amber-700"
                                     : "bg-slate-100 text-slate-600"}`}>
                          {pct.toFixed(1)}%
                        </span>
                      </TD>
                    </TR>
                  );
                })}
              </TBody>
            </Table>
          </Card>
          <Pagination page={data} onPageChange={pagination.setPage} onSizeChange={pagination.setSize} />
        </>
      )}
    </div>
  );
}

// ─── Maintenance ──────────────────────────────────────────────────────────────

function MaintenanceReport() {
  const pagination = usePagination();
  const [filters, setFilters] = useState<MaintenanceListParams>({});
  const { data, loading, error, reload } = useAsyncData(
    () => assetApi.listMaintenance(pagination.page, pagination.size, filters),
    [pagination.page, pagination.size, filters],
  );
  function setF<K extends keyof MaintenanceListParams>(k: K, v: MaintenanceListParams[K]) {
    setFilters((f) => ({ ...f, [k]: v })); pagination.reset();
  }

  const doExport = useCallback(() => {
    downloadXls(`maintenance-${new Date().toISOString().slice(0,10)}.xls`, "Maintenance Log",
      MAINTENANCE_EXPORT_HEADERS, (data?.content ?? []).map(maintenanceToRow));
  }, [data]);

  const content = data?.content ?? [];
  const byStatus = {
    PENDING:     content.filter(m => m.status === "PENDING").length,
    IN_PROGRESS: content.filter(m => m.status === "IN_PROGRESS").length,
    COMPLETED:   content.filter(m => m.status === "COMPLETED").length,
    CANCELLED:   content.filter(m => m.status === "CANCELLED").length,
  };

  return (
    <div className="space-y-4">
      {data && (
        <div className="grid gap-3 sm:grid-cols-4">
          <Stat label="Pending"      value={byStatus.PENDING}     accent="text-amber-700" />
          <Stat label="In Progress"  value={byStatus.IN_PROGRESS} accent="text-blue-700"  />
          <Stat label="Completed"    value={byStatus.COMPLETED}   accent="text-emerald-700" />
          <Stat label="Total Records" value={data.totalElements}  />
        </div>
      )}

      <div className="flex flex-wrap items-center gap-3">
        <FilterPanel activeCount={filters.status ? 1 : 0}
          onClear={() => { setFilters({}); pagination.reset(); }}
          onRefresh={reload}>
          <SelectFilter label="All Statuses" value={filters.status ?? ""} onChange={(v) => setF("status", v)}
            options={[
              { label: "Pending",     value: "PENDING"     },
              { label: "In Progress", value: "IN_PROGRESS" },
              { label: "Completed",   value: "COMPLETED"   },
              { label: "Cancelled",   value: "CANCELLED"   },
            ]} />
        </FilterPanel>
        <ExportBtn onExport={doExport} />
      </div>

      {loading && <AssetLoading label="Loading maintenance records…" />}
      {error   && <AssetError message={error} onRetry={reload} />}

      {!loading && data && (
        <>
          <Card noPad>
            <Table>
              <THead>
                <TH>Asset</TH><TH>Type</TH><TH>Status</TH>
                <TH>Scheduled</TH><TH>Completed</TH>
                <TH>Technician</TH><TH>Vendor</TH><TH right>Cost</TH>
              </THead>
              <TBody>
                {content.length === 0
                  ? <tr><td colSpan={8} className="py-10 text-center text-sm text-slate-400">No maintenance records found.</td></tr>
                  : content.map((m) => (
                  <TR key={m.id}>
                    <TD>
                      <div className="font-semibold text-slate-800">{m.assetName ?? "—"}</div>
                      <div className="font-mono text-xs text-slate-400">{m.assetNumber}</div>
                    </TD>
                    <TD>
                      <span className="rounded-lg bg-amber-50 px-2 py-0.5 text-xs font-semibold text-amber-700">
                        {m.maintenanceType.replace(/_/g," ")}
                      </span>
                    </TD>
                    <TD>
                      <MaintenanceStatusBadge status={m.status} />
                    </TD>
                    <TD muted>{fmtDate(m.scheduledDate)}</TD>
                    <TD muted>{fmtDate(m.completedDate)}</TD>
                    <TD muted>{m.technician ?? "—"}</TD>
                    <TD muted>{m.vendor ?? "—"}</TD>
                    <TD right muted>{m.cost != null ? fmt(m.cost) : "—"}</TD>
                  </TR>
                ))}
              </TBody>
            </Table>
          </Card>
          <Pagination page={data} onPageChange={pagination.setPage} onSizeChange={pagination.setSize} />
        </>
      )}
    </div>
  );
}

// ─── Disposal ─────────────────────────────────────────────────────────────────

function DisposalReport() {
  const pagination = usePagination();
  const [filters, setFilters] = useState<DisposalListParams>({});
  const { data, loading, error, reload } = useAsyncData(
    () => assetApi.listDisposals(pagination.page, pagination.size, filters),
    [pagination.page, pagination.size, filters],
  );
  function setF<K extends keyof DisposalListParams>(k: K, v: DisposalListParams[K]) {
    setFilters((f) => ({ ...f, [k]: v })); pagination.reset();
  }

  const content   = data?.content ?? [];
  const completed = content.filter(d => d.status === "COMPLETED");
  const totalRecovered = completed.reduce((s, d) => s + (d.disposalValue ?? 0), 0);

  const doExport = useCallback(() => {
    downloadXls(`disposals-${new Date().toISOString().slice(0,10)}.xls`, "Asset Disposals",
      DISPOSAL_EXPORT_HEADERS, content.map(disposalToRow));
  }, [content]);

  return (
    <div className="space-y-4">
      {data && (
        <div className="grid gap-4 sm:grid-cols-3">
          <Stat label="Total Records"       value={data.totalElements} />
          <Stat label="Completed Disposals" value={completed.length}   accent="text-slate-600" />
          <Stat label="Value Recovered"     value={fmt(totalRecovered)} accent="text-emerald-700" />
        </div>
      )}

      <div className="flex flex-wrap items-center gap-3">
        <FilterPanel activeCount={filters.status ? 1 : 0}
          onClear={() => { setFilters({}); pagination.reset(); }}
          onRefresh={reload}>
          <SelectFilter label="All Statuses" value={filters.status ?? ""} onChange={(v) => setF("status", v)}
            options={[
              { label: "Pending",          value: "PENDING"   },
              { label: "Approved",         value: "APPROVED"  },
              { label: "Completed",        value: "COMPLETED" },
              { label: "Rejected",         value: "REJECTED"  },
            ]} />
        </FilterPanel>
        <ExportBtn onExport={doExport} />
      </div>

      {loading && <AssetLoading label="Loading disposal records…" />}
      {error   && <AssetError message={error} onRetry={reload} />}

      {!loading && data && (
        <>
          <Card noPad>
            <Table>
              <THead>
                <TH>Asset</TH><TH>Reason</TH><TH>Method</TH>
                <TH>Requested By</TH><TH>Requested</TH>
                <TH>Approved By</TH><TH right>Value</TH><TH>Status</TH>
              </THead>
              <TBody>
                {content.length === 0
                  ? <tr><td colSpan={8} className="py-10 text-center text-sm text-slate-400">No disposal records found.</td></tr>
                  : content.map((d) => (
                  <TR key={d.id}>
                    <TD>
                      <div className="font-semibold text-slate-800">{d.assetName}</div>
                      <div className="font-mono text-xs text-slate-400">{d.assetNumber ?? "—"}</div>
                    </TD>
                    <TD muted><span className="line-clamp-1 max-w-[180px] text-xs">{d.reason}</span></TD>
                    <TD muted>{d.disposalMethod?.replace(/_/g," ") ?? "—"}</TD>
                    <TD muted>{d.requestedBy ?? "—"}</TD>
                    <TD muted>{fmtDate(d.requestedAt)}</TD>
                    <TD muted>{d.approvedBy ?? "—"}</TD>
                    <TD right>
                      {d.disposalValue != null
                        ? <span className="font-medium text-emerald-700">{fmt(d.disposalValue)}</span>
                        : <span className="text-slate-300">—</span>}
                    </TD>
                    <TD><DisposalStatusBadge status={d.status} /></TD>
                  </TR>
                ))}
              </TBody>
            </Table>
          </Card>
          <Pagination page={data} onPageChange={pagination.setPage} onSizeChange={pagination.setSize} />
        </>
      )}
    </div>
  );
}

// ─── Small badge helpers ──────────────────────────────────────────────────────

function MaintenanceStatusBadge({ status }: { status: string }) {
  const cls = {
    COMPLETED:   "bg-emerald-100 text-emerald-700",
    IN_PROGRESS: "bg-blue-100 text-blue-700",
    CANCELLED:   "bg-slate-100 text-slate-500",
    PENDING:     "bg-amber-100 text-amber-700",
    DEFERRED:    "bg-purple-100 text-purple-700",
  }[status] ?? "bg-slate-100 text-slate-600";
  return (
    <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${cls}`}>
      {status.replace(/_/g, " ")}
    </span>
  );
}

function DisposalStatusBadge({ status }: { status: string }) {
  const cls = {
    COMPLETED: "bg-slate-100 text-slate-600",
    APPROVED:  "bg-emerald-100 text-emerald-700",
    REJECTED:  "bg-red-100 text-red-600",
    PENDING:   "bg-amber-100 text-amber-700",
  }[status] ?? "bg-slate-100 text-slate-600";
  return (
    <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${cls}`}>
      {status.replace(/_/g, " ")}
    </span>
  );
}

// ─── Shared constants ─────────────────────────────────────────────────────────

const STATUS_OPTS = [
  { label: "Available",         value: "AVAILABLE"        },
  { label: "Assigned",          value: "ASSIGNED"         },
  { label: "Under Maintenance", value: "UNDER_MAINTENANCE"},
  { label: "Pending Disposal",  value: "PENDING_DISPOSAL" },
  { label: "Disposed",          value: "DISPOSED"         },
  { label: "Lost",              value: "LOST"             },
  { label: "Damaged",           value: "DAMAGED"          },
];
